import React, { useState } from 'react';

const EXPECTED_BREADCRUMB = 'Administration > User Management > Roles & Permissions > Edit: Content Editor';

export default function C09_DeepNav({ onComplete }) {
  const [navState, setNavState] = useState('home'); // home | userMgmt | roles | editRole
  const [adminDropdown, setAdminDropdown] = useState(false);
  const [role, setRole] = useState({ canDelete: false });
  const [saved, setSaved] = useState(false);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const breadcrumb = {
    home: 'Home',
    userMgmt: 'Administration > User Management',
    roles: 'Administration > User Management > Roles & Permissions',
    editRole: EXPECTED_BREADCRUMB,
  }[navState];

  const handleSaveRole = () => {
    setSaved(true);
    setNavState('roles');
  };

  const handleSubmit = () => {
    if (!answer.trim()) return;
    setSubmitted(true);
    const pass = answer.trim() === EXPECTED_BREADCRUMB && role.canDelete && saved;
    onComplete({
      score: pass ? 100 : (answer.trim() === EXPECTED_BREADCRUMB ? 50 : (role.canDelete && saved ? 40 : 0)),
      details: {
        breadcrumb: { expected: EXPECTED_BREADCRUMB, got: answer.trim(), pass: answer.trim() === EXPECTED_BREADCRUMB },
        canDeleteToggled: { expected: 'true', got: String(role.canDelete), pass: role.canDelete },
        roleSaved: { expected: 'true', got: String(saved), pass: saved },
      },
      passed: pass,
    });
  };

  const ROLES = [
    { name: 'Super Admin', perms: 6 },
    { name: 'Content Editor', perms: 3 },
    { name: 'Viewer', perms: 1 },
    { name: 'Moderator', perms: 4 },
  ];

  return (
    <div className="ab-challenge-ui ab-nav-ui">
      {/* Top Nav Bar */}
      <div className="ab-topnav">
        <button className="ab-topnav-item" onClick={() => { setNavState('home'); setAdminDropdown(false); }} disabled={submitted}>Home</button>
        <button className="ab-topnav-item" onClick={() => { setNavState('userMgmt'); setAdminDropdown(false); }} disabled={submitted}>Dashboard</button>
        <div className="ab-topnav-dropdown-wrap">
          <button className="ab-topnav-item ab-topnav-item-active" onClick={() => setAdminDropdown(d => !d)} disabled={submitted}>
            Administration ▾
          </button>
          {adminDropdown && (
            <div className="ab-dropdown">
              <button onClick={() => { setNavState('userMgmt'); setAdminDropdown(false); }}>User Management</button>
              <button onClick={() => { setAdminDropdown(false); }}>System Config</button>
              <button onClick={() => { setAdminDropdown(false); }}>Audit Logs</button>
            </div>
          )}
        </div>
        <button className="ab-topnav-item" onClick={() => { setAdminDropdown(false); }} disabled={submitted}>Reports</button>
      </div>

      {/* Breadcrumb */}
      <div className="ab-breadcrumb">
        {breadcrumb.split(' > ').map((crumb, i, arr) => (
          <span key={i} className="ab-crumb">
            {crumb}
            {i < arr.length - 1 && <span className="ab-crumb-sep"> &rsaquo; </span>}
          </span>
        ))}
      </div>

      {/* Page Content */}
      <div className="ab-nav-content">
        {navState === 'home' && (
          <div className="ab-page-placeholder">
            <div className="ab-page-icon">🏠</div>
            <p>Welcome to the Admin Panel. Use the top navigation to access different sections.</p>
          </div>
        )}

        {navState === 'userMgmt' && (
          <div>
            <h3 className="ab-page-title">User Management</h3>
            <div className="ab-sidebar-layout">
              <div className="ab-sidebar">
                <button className="ab-sidebar-item ab-sidebar-item-active" onClick={() => setNavState('roles')} disabled={submitted}>
                  Roles & Permissions
                </button>
                <button className="ab-sidebar-item" disabled={submitted}>Active Users</button>
                <button className="ab-sidebar-item" disabled={submitted}>Invite Users</button>
                <button className="ab-sidebar-item" disabled={submitted}>Access Logs</button>
              </div>
              <div className="ab-page-placeholder" style={{ flex: 1 }}>
                <p>Select a section from the sidebar.</p>
              </div>
            </div>
          </div>
        )}

        {navState === 'roles' && (
          <div>
            <h3 className="ab-page-title">Roles & Permissions</h3>
            <div className="ab-sidebar-layout">
              <div className="ab-sidebar">
                <button className="ab-sidebar-item ab-sidebar-item-active" disabled={submitted}>Roles & Permissions</button>
                <button className="ab-sidebar-item" disabled={submitted}>Active Users</button>
                <button className="ab-sidebar-item" disabled={submitted}>Invite Users</button>
                <button className="ab-sidebar-item" disabled={submitted}>Access Logs</button>
              </div>
              <div style={{ flex: 1 }}>
                <div className="ab-table-wrap">
                <table className="ab-table">
                  <thead><tr><th>Role Name</th><th>Permissions</th><th>Action</th></tr></thead>
                  <tbody>
                    {ROLES.map(r => (
                      <tr key={r.name}>
                        <td><strong>{r.name}</strong></td>
                        <td>{r.perms} permissions</td>
                        <td>
                          {r.name === 'Content Editor'
                            ? <button className="ab-btn-link" onClick={() => setNavState('editRole')} disabled={submitted}>Edit</button>
                            : <button className="ab-btn-link-disabled" disabled>Edit</button>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {navState === 'editRole' && (
          <div>
            <h3 className="ab-page-title">Edit Role: Content Editor</h3>
            <div className="ab-role-edit-panel">
              <div className="ab-role-perm-row">
                <span>Can View Posts</span>
                <span className="ab-badge ab-badge-green">ON</span>
              </div>
              <div className="ab-role-perm-row">
                <span>Can Create Posts</span>
                <span className="ab-badge ab-badge-green">ON</span>
              </div>
              <div className="ab-role-perm-row">
                <span>Can Edit Posts</span>
                <span className="ab-badge ab-badge-green">ON</span>
              </div>
              <div className="ab-role-perm-row ab-perm-highlight">
                <span><strong>Can Delete Posts</strong></span>
                <label className="ab-toggle">
                  <input type="checkbox" checked={role.canDelete} onChange={e => setRole(r => ({ ...r, canDelete: e.target.checked }))} disabled={saved || submitted} />
                  <span className="ab-toggle-track"><span className="ab-toggle-thumb" /></span>
                  <span className="ab-toggle-txt">{role.canDelete ? 'ON' : 'OFF'}</span>
                </label>
              </div>
              <div className="ab-role-perm-row">
                <span>Can Publish Posts</span>
                <span className="ab-badge ab-badge-red">OFF</span>
              </div>
              <div className="ab-role-perm-row">
                <span>Can Manage Comments</span>
                <span className="ab-badge ab-badge-red">OFF</span>
              </div>
              {!saved
                ? <button className="ab-action-btn" style={{ marginTop: 16 }} onClick={handleSaveRole} disabled={submitted}>Save Role</button>
                : <div className="ab-submitted-badge">✓ Role Saved</div>
              }
            </div>
          </div>
        )}
      </div>

      {saved && (
        <div className="ab-answer-row">
          <label className="ab-label">Enter the full breadcrumb path shown at the top when editing the role:</label>
          <input value={answer} onChange={e => setAnswer(e.target.value)} className="ab-input ab-answer-input"
            placeholder="e.g. Administration > User Management > ..." disabled={submitted} />
          {!submitted
            ? <button className="ab-action-btn" onClick={handleSubmit}>Submit Answer</button>
            : <div className="ab-submitted-badge">✓ Answer Submitted</div>
          }
        </div>
      )}
    </div>
  );
}
